import { useState } from 'react';
import { formatCents, type BidResult, type PackageCode, type PackageQuote } from '@lbhl/shared';
import type { Tier } from '../lib/tiers.js';

interface Props {
  result: BidResult;
  tiers: Tier[];
  isAdmin: boolean;
}

function nameOf(tiers: Tier[], code: PackageCode): string {
  return tiers.find((t) => t.pkg.code === code)?.pkg.name ?? code;
}

/**
 * What the customer sees, and what the office sees behind it.
 *
 * Three prices, because the customer receives all three and chooses afterwards.
 * Each is one all-in number: adjustments and travel are folded in, never itemised
 * for a customer to negotiate against line by line.
 *
 * The internal views are one tier at a time. Three full breakdowns stacked would be
 * several screens of table, and the thing an estimator actually wants is one tier's
 * detail plus the knowledge that the others exist.
 */
export function Quotes({ result, tiers, isAdmin }: Props) {
  const last = result.quotes[result.quotes.length - 1]!;
  const [shown, setShown] = useState<PackageCode>(last.packageCode);
  const quote = result.quotes.find((q) => q.packageCode === shown) ?? last;

  return (
    <>
      <section className="card">
        <h2>What the customer sees</h2>
        <table className="lines">
          <tbody>
            {result.customerLines.map((l) => (
              <tr className="total" key={l.sort}>
                <td>{l.description}</td>
                <td className="num">{formatCents(l.amountCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="note dim">
          One all-in season price per package. The customer picks one; nothing here
          commits them.
        </div>
      </section>

      <section className="card">
        <h2>Internal breakdown</h2>
        <div className="seg">
          {result.quotes.map((q) => (
            <button
              key={q.packageCode}
              type="button"
              aria-pressed={shown === q.packageCode}
              onClick={() => setShown(q.packageCode)}
            >
              {nameOf(tiers, q.packageCode)}
            </button>
          ))}
        </div>

        <table className="lines">
          <tbody>
            {quote.lines.map((l) => (
              <tr key={l.sort}>
                <td>
                  {l.description}
                  <br />
                  <span className="unit" style={{ fontSize: 12, opacity: 0.7 }}>
                    {l.qty} {l.uom} @ {formatCents(l.unitPriceCents)}
                  </span>
                </td>
                <td className="num">{formatCents(l.amountCents)}</td>
              </tr>
            ))}
            {quote.travelFeeCents === 0 && quote.billableTravelMiles > 0 && (
              <tr>
                <td className="dim">
                  Travel ({quote.billableTravelMiles} mi, no rate set)
                </td>
                <td className="num dim">{formatCents(0)}</td>
              </tr>
            )}
            <tr className="total">
              <td>
                {nameOf(tiers, quote.packageCode)} total
                <br />
                <span className="unit" style={{ fontSize: 12, opacity: 0.7 }}>
                  {quote.totalLinearFeet} ft
                  {quote.totalStrands > 0 ? ` · ${quote.totalStrands} strands` : ''}
                </span>
              </td>
              <td className="num">{formatCents(quote.totalCents)}</td>
            </tr>
          </tbody>
        </table>
      </section>

      {isAdmin && (
        <section className="card">
          <h2>Margin — cost inputs only, excludes labour</h2>

          {/* All three, because a higher tier carries more consumables and the
              difference is the thing worth seeing. */}
          <table className="lines">
            <tbody>
              {result.quotes.map((q) => (
                <tr key={q.packageCode} className={q.packageCode === shown ? 'total' : ''}>
                  <td>
                    {nameOf(tiers, q.packageCode)}
                    <br />
                    <span className="unit" style={{ fontSize: 12, opacity: 0.7 }}>
                      cost {formatCents(q.estMaterialCostCents)} · {q.materialsMarginPct}%
                    </span>
                  </td>
                  <td className="num">{formatCents(q.materialsMarginCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3>{nameOf(tiers, quote.packageCode)} cost detail</h3>
          <table className="lines">
            <tbody>
              {quote.materials.map((m) => (
                <tr key={m.sku}>
                  <td className="dim">
                    {m.name}
                    <br />
                    <span style={{ fontSize: 12 }}>
                      {m.qty} {m.uom} @ {formatCents(m.unitCostCents)}
                    </span>
                  </td>
                  <td className="num dim">{formatCents(m.extendedCostCents)}</td>
                </tr>
              ))}
              <tr className="total">
                <td>Estimated cost</td>
                <td className="num">{formatCents(quote.estMaterialCostCents)}</td>
              </tr>
            </tbody>
          </table>
        </section>
      )}

      {result.warnings.length > 0 && isAdmin && (
        <div className="note warn">
          <ul>
            {result.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

/** The three totals for the sticky bar, abbreviated so they fit a phone. */
export function TotalBarFigures({
  quotes,
  tiers,
}: {
  quotes: PackageQuote[];
  tiers: Tier[];
}) {
  return (
    <span className="totals">
      {quotes.map((q) => (
        <span className="one" key={q.packageCode}>
          <span className="cap">{nameOf(tiers, q.packageCode)}</span>
          {formatCents(q.totalCents)}
        </span>
      ))}
    </span>
  );
}
